import React, { createContext, useContext, useState, useCallback } from 'react';

interface TooltipState {
  activeTooltipId: string | null;
  contentMode: 'full' | 'label';
  position: 'top' | 'bottom' | 'left' | 'right';
}

interface TooltipContextValue {
  tooltipState: TooltipState;
  setActiveTooltip: (id: string | null, options?: Partial<Omit<TooltipState, 'activeTooltipId'>>) => void;
  shouldShowTooltip: (id: string) => boolean;
  getTooltipOptions: (id: string) => Omit<TooltipState, 'activeTooltipId'>;
  clearTooltip: () => void;
}

const defaultTooltipState: TooltipState = {
  activeTooltipId: null,
  contentMode: 'full',
  position: 'top'
};

const TooltipContext = createContext<TooltipContextValue | undefined>(undefined);

export function TooltipProvider({ children }: { children: React.ReactNode }) {
  const [tooltipState, setTooltipState] = useState<TooltipState>(defaultTooltipState);
  
  const setActiveTooltip = useCallback((id: string | null, options?: Partial<Omit<TooltipState, 'activeTooltipId'>>) => {
    setTooltipState(prev => ({
      ...prev,
      activeTooltipId: id,
      ...(options || {})
    }));
  }, []);

  const clearTooltip = useCallback(() => {
    setTooltipState(defaultTooltipState);
  }, []);
  
  const shouldShowTooltip = useCallback((id: string) => {
    return id === tooltipState.activeTooltipId;
  }, [tooltipState.activeTooltipId]);
  
  const getTooltipOptions = useCallback((id: string) => {
    if (id !== tooltipState.activeTooltipId) {
      return {
        contentMode: defaultTooltipState.contentMode,
        position: defaultTooltipState.position
      };
    }
    
    return {
      contentMode: tooltipState.contentMode,
      position: tooltipState.position
    };
  }, [tooltipState]);
  
  return (
    <TooltipContext.Provider value={{ 
      tooltipState, 
      setActiveTooltip,
      shouldShowTooltip,
      getTooltipOptions,
      clearTooltip
    }}>
      {children}
    </TooltipContext.Provider>
  );
}

export function useTooltip() {
  const context = useContext(TooltipContext);
  if (context === undefined) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  return context;
} 